/**
 * RequireAdminPage — route guard for one Admin page's per-account level
 * (Editable / Locked / Hidden, read by useAdminPageLevels from a fresh
 * `GET /account/modules`).
 *
 * Wrap the page's routes — its aliases and sub-pages included — in a pathless
 * <Route element={<RequireAdminPage page="<catalog key>" />}>, inside the
 * Admin Access and module guards (a level only narrows them):
 *
 * - levels pending (loading, or the persisted seed alone): render nothing, so
 *   a slow first fetch does not bounce a page the account may have;
 * - Hidden (or no level), or the first fetch failed: redirect to the safe
 *   landing, like a switched-off module page;
 * - Editable or Locked: render the page, with the level in
 *   AdminPageLevelContext for Locked mode.
 *
 * Hold-once: after the page has opened, a later failed refetch (window-focus
 * refetches run with no retry) keeps it rendered at its last fresh level, so a
 * running lead import or an open builder survives a network blip. Only a fresh
 * Hidden takes it away. The hold is kept per page key, because React reuses
 * this component across sibling Admin routes, and every rendering branch
 * returns the same Provider + <Outlet /> so the page is never remounted.
 * Same idea as RequireModule's `holdOnceAllowed`.
 */
import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { AdminPageLevelContext, type ShownAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { adminPageLevelOf, useAdminPageLevels } from '@/hooks/useAdminPageLevels';
import { useSafeLandingPath } from '@/hooks/useNavAccess';

export default function RequireAdminPage({ page }: { page: string }) {
  const { status, levels } = useAdminPageLevels();
  const landing = useSafeLandingPath();
  const [held, setHeld] = useState<{ page: string; level: ShownAdminPageLevel } | null>(null);
  const heldLevel = held?.page === page ? held.level : null;

  let level: ShownAdminPageLevel;
  if (status === 'fresh' && levels) {
    const fresh = adminPageLevelOf(levels, page);
    if (fresh === 'hidden') {
      // Setting state during render is React's pattern for remembering a
      // value from an earlier render; it re-renders once and then settles.
      if (held !== null) setHeld(null);
      return <Navigate to={landing} replace />;
    }
    if (heldLevel !== fresh) setHeld({ page, level: fresh });
    level = fresh;
  } else if (heldLevel !== null) {
    level = heldLevel;
  } else if (status === 'pending') {
    return null;
  } else {
    return <Navigate to={landing} replace />;
  }

  return (
    <AdminPageLevelContext.Provider value={level}>
      <Outlet />
    </AdminPageLevelContext.Provider>
  );
}
